import { Dispatch, SetStateAction, useCallback, useState } from 'react'

type MatchFN<T> = (value: T, index: number) => boolean

type UpdateAction<T> = T | ((prev: T) => T)

type AddOrUpdateAction<T> = T | ((prev: T | undefined) => T)

type ListStateReturnType<T> = {
  list: Array<T>
  setList: Dispatch<SetStateAction<Array<T>>>
  clear: () => void
  isEmpty: () => boolean
  /**
   * Push item to list
   * @param item
   * @returns
   */
  add: (item: T) => void
  /**
   * Add item to list based on provided index
   * @param index
   * @param item
   * @returns
   */
  addAtIndex: (index: number, item: T) => void
  /**
   * add or update to list. matchFn is used to find index item to update, if none match then add operation is executed
   * @param item if function is provided and the operation add is executed then the 'prev' value is always undefined
   * @param matchFn
   * @returns
   */
  addOrUpdate: (item: AddOrUpdateAction<T>, matchFn: MatchFN<T>) => void
  /**
   * update first item that match the matchFn
   * @param item
   * @param matchFn
   * @returns
   */
  update: (item: UpdateAction<T>, matchFn: MatchFN<T>) => void
  /**
   * update item based on provided index. if index is not valid then list state won't change
   * @param index
   * @param item
   * @returns
   */
  updateAtIndex: (index: number, item: UpdateAction<T>) => void
  /**
   * remove first item that match the matchFn
   * @param matchFn
   * @returns
   */
  remove: (matchFn: MatchFN<T>) => void
  /**
   * remove item based on provided index
   * @param index
   * @returns
   */
  removeAtIndex: (index: number) => void
}

const useListState = <T>(initial: Array<T>): ListStateReturnType<T> => {
  const [list, setList] = useState<Array<T>>(initial)

  const isEmpty = useCallback(() => {
    if (list.length > 0) return false
    else return true
  }, [list.length])

  const add = useCallback((item: T) => {
    setList((prev) => [...prev, item])
  }, [])

  const addAtIndex = useCallback((index: number, item: T) => {
    setList((prev) => [...prev.slice(0, index), item, ...prev.slice(index)])
  }, [])

  const addOrUpdate = useCallback((item: AddOrUpdateAction<T>, matchFn: MatchFN<T>) => {
    setList((prev) => {
      const index = prev.findIndex((v, i) => matchFn(v, i))
      const isAdd = index === -1

      if (isAdd) {
        const newValue = item instanceof Function ? item(undefined) : item

        return [...prev, newValue]
      } else {
        const newValue = item instanceof Function ? item(prev[index]) : item

        prev[index] = newValue

        return [...prev]
      }
    })
  }, [])

  const update = useCallback((item: UpdateAction<T>, matchFn: MatchFN<T>) => {
    setList((prev) => {
      const clone = [...prev]

      for (let i = 0; i < clone.length; i++) {
        if (matchFn(clone[i], i)) {
          if (item instanceof Function) {
            clone[i] = item(clone[i])
          } else {
            clone[i] = item
          }
          break
        }
      }

      return clone
    })
  }, [])

  const updateAtIndex = useCallback((index: number, item: UpdateAction<T>) => {
    setList((prev) => {
      const clone = [...prev]

      if (item instanceof Function) {
        clone[index] = item(clone[index])
      } else {
        clone[index] = item
      }

      return clone
    })
  }, [])

  const remove = useCallback((matchFn: MatchFN<T>) => {
    setList((prev) => {
      let isRemoved = false

      return prev.filter((v, i) => {
        if (matchFn(v, i) && !isRemoved) {
          isRemoved = true
          return false
        }
        return true
      })
    })
  }, [])

  const removeAtIndex = useCallback((index: number) => {
    setList((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev
      }

      return [...prev.slice(0, index), ...prev.slice(index + 1)]
    })
  }, [])

  const clear = useCallback(() => {
    setList([])
  }, [])

  return {
    list,
    setList,
    isEmpty,
    add,
    addAtIndex,
    addOrUpdate,
    update,
    updateAtIndex,
    remove,
    removeAtIndex,
    clear,
  }
}

export type { ListStateReturnType, MatchFN }

export { useListState }
